import React, {useState, useEffect} from 'react';
import dayjs from 'dayjs';
import {
  Smartphone,
  AlertCircle,
  CheckCircle,
  ExternalLink,
  Mail,
  MapPin,
} from 'lucide-react';

import {useIsAdmin} from '../state/hooks';
import {Role, DeviceType, ClientInfoQuery} from '../net/types/graphql';
import {useUpdateFsmState} from '../net/query/UpdateFsmState.gql';
import {useClientInfoCtx} from '../ClientInfoProvider';
import {formatPhoneNumber} from '../util/phoneNumber';
import {StateEditor} from '../StateEditor';
import {Button} from '../components/ui/button';
import {Title} from '../components/ui/typography';
import {useToast} from '../components/ui/toast';
import {confirmDialog} from '../util/confirm-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {Alert, AlertDescription} from '../components/ui/alert';
import {Separator} from '../components/ui/separator';

type ClientInfo = Extract<
  ClientInfoQuery['node'],
  {__typename?: 'ClientAccount'}
>;

/**
 * Get a formatted phone number for the client.
 */
const getPhoneNumber = (clientInfo: ClientInfo) => {
  const {devices} = clientInfo;

  const phone = devices?.find((d) => d.type === DeviceType.Mobile);
  if (!phone?.address) {
    return <span className="text-destructive">No phone number available.</span>;
  }

  const pn = formatPhoneNumber(phone.address, 'US');

  if (!pn.valid) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-destructive cursor-help">
              {phone.address}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p>This phone number is invalid: {pn.error}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return <span className="font-semibold">{pn.text}</span>;
};

/**
 * Test whether a string is actualy a hyperlink.
 *
 * Link might be a deep link to an arbitrary app like `nudge://client/1234`.
 */
const isRealLink = (s: string) => {
  return /^[a-z][a-z0-9_]*:\/\/[^\s]+$/i.test(s);
};

const infoClassNames =
  'text-sm h-full flex items-center justify-center px-[0.5rem] basis-full overflow-hidden text-ellipsis whitespace-nowrap';

const infoClassBorder = ' border-l-(--border) border-l';

/**
 * View for information about the recipient of the chat.
 */
export const ChatInfo = () => {
  const {loading, error, clientInfo} = useClientInfoCtx();
  if (loading) {
    return null;
  }
  if (error || !clientInfo) {
    return <>Error loading client info: {error?.message}</>;
  }

  return (
    <>
      <div
        title={`${clientInfo.fullName}, ${
          clientInfo.gender || 'unknown gender'
        }`}
        className={infoClassNames}
      >
        <span>{clientInfo.fullName}</span>
        {clientInfo.gender ? (
          <>
            {'\u00A0'}
            <span className="text-muted-foreground">({clientInfo.gender})</span>
          </>
        ) : null}
      </div>
      <div className={infoClassNames + infoClassBorder}>
        {getPhoneNumber(clientInfo)}
      </div>
    </>
  );
};

/**
 * Get hash of info using the "external app link" from the org adapter.
 *
 * The link might be a real link to some CMS (e.g., Salesforce), or it might
 * be a custom string. If the custom string looks like it contains a label,
 * then format it accordingly.
 */
const getIDInfo = (appLink: string) => {
  if (isRealLink(appLink)) {
    return {
      label: 'Profile',
      value: (
        <Button variant="link" className="p-0 h-auto" asChild>
          <a href={appLink} target="_blank" rel="noopener noreferrer">
            View client profile
            <ExternalLink className="ml-1 h-3 w-3" />
          </a>
        </Button>
      ),
      help: "Link to client's information in your organization's CMS",
    };
  }

  // If the link includes its own label, parse it out.
  if (/^[\w\d\s-]+:\s*.*$/.test(appLink)) {
    const [label, value] = appLink.split(':', 2);
    return {
      label,
      value: value.trim(),
      help: "Client's ID in your organization's CMS",
    };
  }

  return {
    label: 'ID',
    value: appLink,
    help: "Client's ID in your organization's CMS",
  };
};

/**
 * Create a link to Google Maps for the given address.
 */
export const googleMapsLink = (s: string | null | undefined) => {
  if (!s) {
    return null;
  }

  const link = `https://maps.google.com/?q=${encodeURIComponent(s)}`;
  return (
    <a
      href={link}
      className="m-0 items-center text-primary flex gap-1"
      target="_blank"
      rel="noopener noreferrer"
      title={s}
    >
      <MapPin className="m-0 h-3 w-3 flex-shrink-0" />
      <span className="text-ellipsis overflow-hidden whitespace-nowrap">
        {s}
      </span>
    </a>
  );
};

/**
 * Create a mailto link.
 */
export const emailLink = (s: string | null | undefined) => {
  if (!s) {
    return null;
  }

  const link = `mailto:${s}`;
  return (
    <a
      href={link}
      className="m-0 items-center text-primary flex gap-1"
      target="_blank"
      rel="noopener noreferrer"
    >
      <Mail className="m-0 h-3 w-3" />
      <span>{s}</span>
    </a>
  );
};

/**
 * Display a short non-interactive version of the FSM state.
 */
const InlineFsmState = ({state}: {state: string}) => {
  if (!state) {
    return <span className="text-muted-foreground">[none]</span>;
  }

  const parts = state.split('|');
  return (
    <ul className="list-none p-0 m-0 flex gap-1 flex-col items-start">
      {parts.map((part, i) => (
        <li key={i}>
          <code className="px-1 py-0.5 bg-muted rounded-md">{part}</code>
        </li>
      ))}
    </ul>
  );
};

/**
 * Info about the client that there's not room for in the main screen.
 */
export const ChatMoreInfo = () => {
  const isAdmin = useIsAdmin();
  const {showToast} = useToast();
  const {loading, error, clientInfo, featureAssignments, fsm} =
    useClientInfoCtx();
  const fsmStates = fsm?.meta?.nestedStates || [];
  const [initialFsmState, setInitialFsmState] = useState('');
  const [
    updateFsmState,
    {error: errorUpdateFsmState, loading: loadingUpdateFsmState},
  ] = useUpdateFsmState();

  useEffect(() => {
    const s = clientInfo?.fsmState || '';
    setInitialFsmState(s);
  }, [clientInfo?.fsmState]);

  if (loading) {
    return null;
  }

  if (error || !clientInfo) {
    return <>Error loading client info: {error?.message}</>;
  }

  const twilioPhoneNumber = formatPhoneNumber(clientInfo.twilioNumber, 'US');

  const contactInfo = [
    getIDInfo(clientInfo.externalAppLink || ''),
    {
      label: 'Language',
      value: clientInfo.language || 'No language preference',
      help: 'The language the client prefers to communicate in.',
    },
    {
      label: 'E-mail',
      value: emailLink(clientInfo.email),
      help: 'This is the e-mail address on file for this client.',
    },
    {
      label: 'Address',
      value: googleMapsLink(clientInfo.address),
      help: 'This is the physical address on file for this client.',
    },
    {
      label: 'Send from number',
      value: twilioPhoneNumber.valid ? (
        twilioPhoneNumber.text
      ) : (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-destructive cursor-help">
                {twilioPhoneNumber.text}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Invalid phone number: {twilioPhoneNumber.error}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
      help: 'The messages you are sending will come from this phone number.',
    },
  ];

  return (
    <div className="nudge-more-info-inner p-4 space-y-6">
      <div>
        {!clientInfo.isActive && (
          <Alert className="mb-4" variant="warning">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              We failed to fetch data about this person. You may still try to
              communicate with them if needed, but be aware that the information
              you see here may be outdated.
            </AlertDescription>
          </Alert>
        )}
        <div className="space-y-3">
          {contactInfo.map((item, index) => (
            <div key={index} className="flex justify-between items-start">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-muted-foreground text-sm cursor-help">
                      {item.label}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{item.help}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <div className="text-right text-sm max-w-[200px]">
                {item.value || '--'}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Separator className="my-6" />

      <div>
        <Title level={5} className="mb-3">
          <div className="flex gap-3">
            <span>👩‍⚖️</span>
            <span>Assigned attorneys</span>
          </div>
        </Title>
        <div className="space-y-2 ml-4">
          {clientInfo.assignedStaff.map((item, index) => {
            const nudgeUser = item.roles.includes(Role.Staff);
            const color = nudgeUser
              ? 'text-(--constructive)'
              : 'text-(--destructive)';
            const hoverText = nudgeUser
              ? `${item.fullName} uses Nudge!`
              : `${item.fullName} does not currently have access to Nudge.`;
            return (
              <div
                key={index}
                className="flex items-center space-x-2"
                title={hoverText}
              >
                <div
                  className={`${color} ${nudgeUser ? 'visible' : 'invisible'}`}
                >
                  <Smartphone className="h-4 w-4" />
                </div>
                <span className={`text-sm ${color}`}>{item.fullName}</span>
              </div>
            );
          })}
        </div>
      </div>

      <Separator className="my-6" />

      <div>
        <Title level={5} className="mb-3">
          <div className="flex gap-3">
            <span>📆</span>
            <span>Court appearances</span>
          </div>
        </Title>
        <div className="space-y-3">
          {clientInfo.courtDates.map((item, index) => (
            <div key={index} className="border-l-2 border-muted pl-3">
              <div className="flex items-start space-x-2">
                <div className="mt-1">
                  {item.confirmedDate ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            Client confirmed they will attend on{' '}
                            {dayjs(item.confirmedDate).format('LLLL')}.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <AlertCircle className="h-4 w-4 text-destructive" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Client has not yet confirmed attendance.</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-sm">
                    {dayjs(item.courtDate).format('llll')}
                  </div>
                  <div className="text-xs text-muted-foreground space-y-1">
                    <div>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help">
                              Case Number: {item.caseNumber}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Top charge: {item.topCharge}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div>{item.courtLocation}</div>
                    {item.courtRoom && <div>Room: {item.courtRoom}</div>}
                    {item.online && <div>Online</div>}
                    {item.notes && (
                      <div className="bg-yellow-100 dark:bg-yellow-900/20 px-2 py-1 rounded text-xs">
                        {item.notes}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {isAdmin && (
        <>
          <Separator className="my-6" />
          <div>
            <Title level={5} className="mb-3">
              <div className="flex gap-3">
                <span>⚙️</span>
                <span>Client state</span>
              </div>
            </Title>
            <div className="space-y-4">
              <div className="flex justify-between items-center w-full">
                <div className="min-w-[70px] shrink-0">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-muted-foreground text-sm cursor-help">
                          FSM State
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Inspect the state of the finite state machine.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div>
                  <StateEditor
                    states={fsmStates}
                    current={initialFsmState}
                    error={errorUpdateFsmState}
                    loading={loadingUpdateFsmState}
                    onUpdate={(value) => {
                      if (value !== initialFsmState) {
                        confirmDialog.confirm({
                          title: (
                            <span className="text-secondary">
                              Are you sure?
                            </span>
                          ),
                          description: (
                            <>
                              <div className="my-2 text-sm">
                                This will update the FSM state as follows:
                                <div className="flex gap-4 items-center mx-4 text-(--destructive) my-2">
                                  <label className="text-right font-bold min-w-[100px] shrink-0 grow-0">
                                    Old state:
                                  </label>
                                  <InlineFsmState state={initialFsmState} />
                                </div>
                                <div className="flex gap-4 items-center mx-4 text-(--constructive) my-2">
                                  <label className="text-right font-bold min-w-[100px] shrink-0 grow-0">
                                    New state:
                                  </label>
                                  <InlineFsmState state={value} />
                                </div>
                              </div>
                              <div className="my-2">
                                <strong>
                                  Using the wrong state may interfere with
                                  automated messaging!
                                </strong>
                              </div>
                              <Separator />
                            </>
                          ),
                          confirmText: 'Update',
                          cancelText: 'Cancel',
                          onConfirm: () => {
                            if (clientInfo.__typename !== 'ClientAccount') {
                              return;
                            }
                            updateFsmState({
                              update: () => {
                                setInitialFsmState(value);
                                showToast(
                                  `Updated FSM state to "${value}!"`,
                                  'success',
                                );
                              },
                              variables: {
                                accountId: clientInfo.id,
                                newState: value,
                              },
                            });
                          },
                          onCancel: () => {
                            showToast(
                              `Keeping FSM state "${initialFsmState}."`,
                              'warning',
                            );
                          },
                        });
                      }
                    }}
                  />
                </div>
              </div>

              <div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-muted-foreground text-sm cursor-help">
                        Feature assignments
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        Feature gate values, including randomized group
                        assignments.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <div className="mt-2 space-y-2 ml-4">
                  {(featureAssignments?.slice() || []).map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center space-x-2 text-sm justify-between"
                    >
                      <span
                        className="font-medium min-w-[100px] shrink-0 grow-0"
                        title={item.featureName}
                      >
                        {item.featureName}
                      </span>
                      <span
                        className="text-muted-foreground"
                        title={`Value: ${item.value}`}
                      >
                        {item.variant}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
